/**
 * The `mentions` knowledge layer — soft, party-to-party relationship
 * edges mined from our own data sources (Fireflies call transcripts and emails).
 *
 * A row is the atomic unit: "in source X, an asserter stated that party FROM has
 * relationship R with party TO (optionally facilitated by party VIA)." Unlike the
 * retired speaker→target `mentions` model, the edge between two mentioned parties
 * is first-class — so "a16z introduced Jonathan" is one row, not two disconnected
 * name-drops. Every row here is SOFT/hearsay by nature; it is graph-usable but is
 * never surfaced as a hard CRM relationship. A future iteration sifts these and
 * promotes high-confidence, Dwight-relevant edges into the CRM tables (referral /
 * investment / person_org_company_relationship).
 *
 * NOTE: distinct from `MentionType` in ./mentions.ts, which models @-mentions
 * (tagging a client/user/entity inside a Note or Arc chat). Different concept,
 * different table (`note_mentions`).
 */

/**
 * Where an edge was mined from. Stored as plain text on the row (not a pg enum)
 * so adding a new source — notes, Box docs — needs no migration.
 */
export enum MentionSourceType {
  FirefliesTranscript = 'fireflies_transcript',
  Email = 'email',
}

/**
 * What kind of CRM entity a role (asserter/from/to/via) resolved to. `Unknown`
 * means the reference is held as raw text only (no confident CRM match yet) —
 * these are resolved over time by the ResolveMentions backfill. The
 * asserter is realistically only `Person` / `User` (+ `Company` affiliation);
 * from/to/via span the full set.
 */
export enum MentionEntityKind {
  Person = 'person',
  Company = 'company',
  Organization = 'organization',
  Client = 'client',
  User = 'user',
  Unknown = 'unknown',
}

/** Lifecycle of an edge row. */
export enum MentionStatus {
  /** Live, counts toward derived graph signals. */
  Active = 'active',
  /** Hidden/ignored by a reviewer; retained for audit. */
  Dismissed = 'dismissed',
  /** Superseded by a later re-extraction of the same source. */
  Merged = 'merged',
}

/**
 * How the edge captured relative to the graph. Drives filtering: `relationship`
 * edges are the referral-graph signal; `commercial` edges are retained as
 * affiliation signal (prevalence-discounted downstream, e.g. KeHE/UNFI);
 * `incidental` name-drops/comparisons stay out of derived signals.
 */
export enum MentionClass {
  Relationship = 'relationship',
  Commercial = 'commercial',
  Incidental = 'incidental',
}

/**
 * Controlled relationship vocabulary. Text on the row (per the repo's
 * text-not-enum convention), but constrained to this canonical set at parse
 * time — unknown values degrade to `Other`. Each type's class, directionality,
 * via-eligibility, and future hard-CRM mapping live in RELATIONSHIP_TYPE_CATALOG
 * (common/constants/relationshipTypeCatalog.ts), NOT as row columns.
 */
export enum RelationshipType {
  /** Warm intro facilitated (strongest referral intensity). Uses `via` = introducer. */
  Introduced = 'introduced',
  /** Actionably pointed at us/someone. May use `via` = referrer. */
  ReferredBy = 'referred_by',
  /** Radar-only name-drop of a relationship (weakest referral intensity). */
  MentionedBy = 'mentioned_by',
  InvestedIn = 'invested_in',
  AdvisorTo = 'advisor_to',
  BoardMember = 'board_member',
  WorksAt = 'works_at',
  FormerEmployer = 'former_employer',
  Knows = 'knows',
  Colleague = 'colleague',
  WorksWith = 'works_with',
  // ── Commercial (mention_class = commercial) ──
  Retailer = 'retailer',
  SalesChannel = 'sales_channel',
  Customer = 'customer',
  Distributor = 'distributor',
  Supplier = 'supplier',
  // ── Incidental ──
  Comparison = 'comparison',
  /** Fallback when the model emits a relationship we don't recognize. */
  Other = 'other',
}

/** Whether `from`/`to` order is meaningful (directed) or interchangeable (symmetric). */
export type RelationshipDirection = 'directed' | 'symmetric'

/**
 * The raw, pre-resolution output for one extracted edge (the LLM output). The
 * persist step resolves each `*RawName` to a CRM entity, derives `class` from
 * the catalog, computes the dedupe key, and maps this onto a persisted row.
 */
export interface IExtractedMention {
  // Asserter — who stated it (speaker/sender) + the company they spoke from.
  asserterRawName: string | null
  asserterAffiliation: string | null
  // From party — subject / origin of a directed edge (always present).
  fromRawName: string
  fromKindGuess: MentionEntityKind | string
  fromAffiliation: string | null
  // To party — object / destination; null for a plain name-drop.
  toRawName: string | null
  toKindGuess: MentionEntityKind | string
  toAffiliation: string | null
  // Via party — facilitator (introducer / referrer); null unless applicable.
  viaRawName: string | null
  viaKindGuess: MentionEntityKind | string
  viaAffiliation: string | null
  // Edge semantics.
  relationshipType: RelationshipType | string
  /** Verbatim clause asserting THIS edge (exact source text, not a paraphrase). */
  verbatimQuote: string | null
  /** Verbatim ±N-sentence window around the reference, pulled from the source. */
  contextSnippet: string | null
  /** Deep-link ref into the source (Fireflies sentence index, email line marker). */
  snippetRef: string | null
  /** Co-mention cluster tag within the source (same moment => same group). */
  groupKey: string | null
  sentiment: string | null
  /** Model confidence in the edge, 0–1. Soft by nature. */
  confidence: number | null
}

/**
 * A persisted edge row. Mirrors the `Mention` entity (server-side).
 * Every role FK is nullable: a role can reference something not yet in the CRM,
 * in which case only `*RawName` + `kind = Unknown` are populated.
 */
export interface IMention {
  id: string

  // Source / provenance
  sourceType: MentionSourceType | string
  sourceId: string
  sourceSnippetRef: string | null
  contextSnippet: string | null
  verbatimQuote: string | null
  occurredAt: Date | string | null
  groupKey: string | null

  // Roles, flattened and prefixed asserter/from/to/via
  asserterKind: MentionEntityKind
  asserterPersonId: string | null
  asserterUserId: string | null
  asserterCompanyId: string | null
  asserterRawName: string | null
  asserterAffiliation: string | null

  fromKind: MentionEntityKind
  fromPersonId: string | null
  fromCompanyId: string | null
  fromOrganizationId: string | null
  fromClientId: string | null
  fromUserId: string | null
  fromRawName: string
  fromAffiliation: string | null
  fromResolutionConfidence: number | null

  toKind: MentionEntityKind
  toPersonId: string | null
  toCompanyId: string | null
  toOrganizationId: string | null
  toClientId: string | null
  toUserId: string | null
  toRawName: string | null
  toAffiliation: string | null
  toResolutionConfidence: number | null

  viaKind: MentionEntityKind
  viaPersonId: string | null
  viaCompanyId: string | null
  viaOrganizationId: string | null
  viaClientId: string | null
  viaUserId: string | null
  viaRawName: string | null
  viaAffiliation: string | null
  viaResolutionConfidence: number | null

  // Edge semantics
  relationshipType: RelationshipType | string
  mentionClass: MentionClass
  extractionConfidence: number | null
  sentiment: string | null

  // Provenance / lifecycle
  extractedBy: string | null
  agentRunId: string | null
  status: MentionStatus
  dedupeKey: string

  createdAt: Date | string
  updatedAt: Date | string
}

/** The four parties on an edge, in the order the review table reads them. */
export type MentionRole = 'asserter' | 'from' | 'to' | 'via'

/** Roles that can point at any CRM kind (the asserter is always a person). */
export type MentionPartyRole = 'from' | 'to' | 'via'

/**
 * One role of an edge, flattened for display. Computed server-side so the review
 * table doesn't reimplement the four-role fan-out over 20-odd prefixed columns.
 */
export interface IMentionRole {
  role: MentionRole
  rawName: string | null
  kind: MentionEntityKind
  affiliation: string | null
  confidence: number | null
  resolvedId: string | null
  /**
   * Display name of the CRM record this role resolved to. Null when unresolved,
   * or when the record has no name to show. Distinct from `rawName`, which is
   * the name as it was actually said.
   */
  resolvedName: string | null
  isResolved: boolean
  isLowConfidence: boolean
}

/** A row in the CRM mention-review table. */
export interface IMentionRow extends IMention {
  /** 'Call' or 'Email'. */
  sourceLabel: string
  /**
   * Deep link back to the Fireflies transcript or Gmail thread, when the source
   * can be resolved to one. `null` renders the label as plain text.
   */
  sourceUrl: string | null
  /** Worst state across the row's named roles — drives the queue's grouping. */
  resolutionState: string
  /** Named roles only; a role with no raw name was never mentioned. */
  roles: IMentionRole[]
  asserterUser: { id: string; firstName: string; lastName: string } | null
}
